import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  useTheme,
  Divider,
} from '@mui/material';
import { EmojiEvents as TrophyIcon, AccessTime as TimeIcon, LocationOn as LocationIcon, Event as EventIcon } from '@mui/icons-material';
import { eventService, StoreEvent } from '../../services/eventService';
import SEO from '../../components/SEO';

const TournamentsPage: React.FC = () => {
  const [tournaments, setTournaments] = useState<StoreEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const data = await eventService.getUpcomingEvents('tournament');
        setTournaments(data);
      } catch (error) {
        console.error('Error fetching tournaments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTournaments();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <SEO title="Tournaments" description="Join competitive tournaments and win exclusive prizes." />
      
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TrophyIcon color="warning" sx={{ fontSize: 40 }} />
        <Typography variant="h4" fontWeight="bold">
          Upcoming Tournaments
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : tournaments.length === 0 ? (
        <Box 
          sx={{ 
            py: 10, 
            textAlign: 'center', 
            borderRadius: 4, 
            background: 'rgba(255,255,255,0.01)', 
            border: '1px dashed rgba(255,255,255,0.05)' 
          }}
        >
          <TrophyIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No upcoming tournaments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for competitive events and prize tournaments.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {tournaments.map((tournament) => (
            <Grid item xs={12} md={6} key={tournament.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderTop: `4px solid ${theme.palette.warning.main}` }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                      {tournament.title}
                    </Typography>
                    {tournament.entryFee && (
                      <Chip label={`Entry: ${tournament.entryFee}`} color="warning" variant="filled" size="small" />
                    )}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {tournament.description}
                  </Typography>

                  {tournament.prizes && (
                    <Box sx={{ mt: 2, mb: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="warning.main" gutterBottom>
                        🏆 Prizes
                      </Typography>
                      <Typography variant="body2">
                        {tournament.prizes}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" alignItems="center" gap={1} color="text.secondary">
                      <EventIcon fontSize="small" />
                      <Typography variant="body2">
                        {new Date(tournament.date).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} color="text.secondary">
                      <TimeIcon fontSize="small" />
                      <Typography variant="body2">{tournament.time}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} color="text.secondary">
                      <LocationIcon fontSize="small" />
                      <Typography variant="body2">{tournament.location}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default TournamentsPage;
